import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import CustomTextField from "../components/CustomTextField";
import CustomDropdown from "../components/CustomDropdown";
import CustomButton from "../components/CustomButton";
import ReusableHeader from "./ReusableHeader";
import "../styles/layout.css";
import { toast } from "react-toastify";
import { registerVendorUser, updateVendorUser, getVendorUserById } from "../api/users";
import { useOrganizationApi } from "../api/organization";
import { useUsersApi } from "../api//users"
import { decodeJwt } from "../util/decodeJwt";
import OrganizationCreateDialog from "./OrganizationCreateDialog";
import { useDirectCustomerApi } from "../api/direct_customers";
import { validatePassword, validatePhoneNumber } from "../util/validation";

export default function UserCreateOrUpdateWidget({ userId, userRole, onSubmit, onCancel }) {
  const { getVendorUserById, updateVendorUser, registerVendorUser } = useUsersApi();
  const { getDirectCustomerById, updateDirectCustomer, createDirectCustomer } = useDirectCustomerApi();
  const { getAllVendorOrg } = useOrganizationApi();
  const isEditMode = Boolean(userId);
  const id = userId;
  const navigate = useNavigate();


  const [formData, setFormData] = useState({
    name: "",
    phoneNumber: "",
    password: "",
    role: "",
    address: "",
  });
  const [loading, setLoading] = useState(false);
  const [orgOptions, setOrgOptions] = useState([]);
  const [loggedInUserId, setLoggedInUserId] = useState("");
  const [currentUserRole, setCurrentUserRole] = useState("");

  // ✅ New state for organization dialog
  const [isOrgDialogOpen, setIsOrgDialogOpen] = useState(false);
  const handleOrgDialogOpen = () => setIsOrgDialogOpen(true);
  const handleOrgDialogClose = () => setIsOrgDialogOpen(false);

  const handleOrgCreated = (newOrg) => {
    // Update orgOptions with newly created org
    setOrgOptions((prev) => [...prev, { value: newOrg._id, label: newOrg.name }]);
    // Auto-select the new org
    setFormData((prev) => ({ ...prev, organizationId: newOrg._id }));
    setIsOrgDialogOpen(false);
  };

  // Decode JWT to get current user id + role
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = decodeJwt(token);
        if (decoded?._id) setLoggedInUserId(decoded._id);
        if (decoded?.role) setCurrentUserRole(decoded.role.toLowerCase());
      } catch (err) {
        console.error("JWT decode failed", err);
      }
    }
  }, []);

  // Role dropdown options based on logged user role
  const getRoleOptions = () => {
    switch (currentUserRole) {
      case "admin":
        return [
          { value: "manufacturer", label: "Manufacturer" },
        ];
      case "manufacturer":
        return [
          { value: "distributor", label: "Distributor" },
          { value: "dealer", label: "Dealer" },
          { value: "customer", label: "Customer" }
        ];
      case "distributor":
        return [
          { value: "dealer", label: "Dealer" },
          { value: "customer", label: "Customer" }
        ];
      case "dealer":
        return [{ value: "customer", label: "Customer" }];
      default:
        return [];
    }
  };

  // Auto-select Role if only one option
  useEffect(() => {
    const roles = getRoleOptions();
    if (roles.length === 1 && !formData.role) {
      setFormData((prev) => ({ ...prev, role: roles[0].value }));
    }
  }, [currentUserRole]);

  // ✅ Fetch organizations
  useEffect(() => {
    async function loadOrganizations() {
      if (!userId || !currentUserRole) return;
      try {
        const result = await getAllVendorOrg({ limit: 50, offset: 0, userId });

        // 🔥 Filter out orgs where the type matches current user's role
        const filteredOrgs = result.data.filter(
          (org) => org.type?.toLowerCase() !== currentUserRole
        );

        const options = filteredOrgs.map((org) => ({
          value: org._id,
          label: org.name,
        }));

        setOrgOptions(options);

        // ✅ Auto-select if only one option
        if (options.length === 1) {
          setFormData((prev) => ({
            ...prev,
            organizationId: options[0].value,
          }));
        }
      } catch (err) {
        console.error("Failed to load organizations", err);
        toast.error("Failed to load organizations");
      }
    }
    loadOrganizations();
  }, [userId, currentUserRole]);

  // Fetch user data if editing
  useEffect(() => {
    async function loadUser() {
      if (!isEditMode) return;
      try {
        setLoading(true);

        let user;
        if (userRole === "direct_customer_owner") {
          const res = await getDirectCustomerById(id);
          user = res.data;
        } else {
          user = await getVendorUserById(id);
        }

        setFormData({
          name: user.name || "",
          phoneNumber: user.phoneNumber || "",
          password: "",
          role: user.role === "direct_customer_owner" ? "customer" : (user.role || ""),
          address: (user.organization?.address || user.address) || "",
        });
      } catch (err) {
        console.error(err);
        toast.error("Failed to load user data");
      } finally {
        setLoading(false);
      }
    }
    loadUser();
  }, [id, isEditMode, userRole]);

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const validateForm = () => {

    // ✅ NAME
    if (!formData.name?.trim()) {
      toast.error("Name is required");
      return false;
    }

    // ✅ PHONE
    if (!formData.phoneNumber?.trim()) {
      toast.error("Phone number is required");
      return false;
    }

    if (!validatePhoneNumber(formData.phoneNumber)) {
      toast.error("Phone number must be 10 digits");
      return false;
    }

    // ✅ PASSWORD
    // Create → required
    // Edit → optional (validate only if entered)
    if (!isEditMode && !formData.password?.trim()) {
      toast.error("Password is required");
      return false;
    }

    if (formData.password?.trim() && !validatePassword(formData.password)) {
      toast.error(
        "Password must be 8+ characters with at least one letter and one number"
      );
      return false;
    }

    // ✅ ROLE
    if (!formData.role) {
      toast.error("Role is required");
      return false;
    }

    // ✅ ADDRESS
    if (!formData.address?.trim()) {
      toast.error("Address is required");
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      let result;

      if (isEditMode) {
        const payload = { ...formData };
        if (!payload.password) delete payload.password;

        console.log("DEBUG: Sending Update Payload:", payload);

        if (userRole === "direct_customer_owner") {
          result = await updateDirectCustomer(id, payload);
        } else {
          result = await updateVendorUser(id, payload);
        }
        toast.success("User updated successfully!");
      } else {
        if (formData.role === "customer") {
          result = await createDirectCustomer(formData);
        } else {
          result = await registerVendorUser(formData);
        }
        toast.success("User registered successfully!");
      }

      if (onSubmit) onSubmit(result.data || formData);
      else navigate("/users");

    } catch (err) {
      toast.error(err.message || "Operation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="user-container">
      <div className="modal-header">
        {isEditMode ? "Edit User" : "Create User"}
      </div>
      <div className="user-form-card">
        <div className="user-columns">
          <div className="user-column-left">
            <CustomTextField
              label="Name"
              value={formData.name}
              onChange={(e) => {
                console.log("DEBUG: Name changed to:", e.target.value);
                handleChange("name", e.target.value);
              }}
            />
            <CustomTextField
              label="Phone Number"
              value={formData.phoneNumber}
              onChange={(e) => {
                console.log("DEBUG: Phone changed to:", e.target.value);
                handleChange("phoneNumber", e.target.value);
              }}
            />
            <CustomTextField
              label="Password"
              type="password"
              value={formData.password}
              onChange={(e) => {
                console.log("DEBUG: Password changed (length):", e.target.value.length);
                handleChange("password", e.target.value);
              }}
            />
          </div>

          <div className="user-column-right">
            <CustomDropdown
              label="Role"
              options={getRoleOptions()}
              value={formData.role}
              onChange={(value) => handleChange("role", value)}
              isMultiSelect={false}
            />
            <CustomTextField
              label="Address"
              value={formData.address}
              onChange={(e) => {
                console.log("DEBUG: Address changed to:", e.target.value);
                handleChange("address", e.target.value);
              }}
            />
          </div>
        </div>

        <div className="user-buttons">
          <CustomButton
            label="Cancel"
            className="user-cancel"
            onClick={() => {
              if (onCancel) {
                onCancel();   // close modal only
              } else {
                navigate("/users");
              }
            }}
          />
          <CustomButton
            label={loading ? (isEditMode ? "Updating..." : "Creating...") : isEditMode ? "Update" : "Create"}
            className="user-create"
            onClick={handleSubmit}
            disabled={loading}
          />
        </div>
      </div>
    </div>
  );
}
